import {useApp} from "../../app.context";
import StringInputLabel from "../../elements/stringInputLabel.element";
import {useState} from "react";
import {useIntegrator} from "../../integrator.context";
import DiffView from "../diff/diffView.view";

function NotificationView() {
    const {branch, branches, applyBranch, totalChanges} = useIntegrator();
    const {addBranch, removeBranch, setViewBranch, confirmDelete, confirm, setNotification} = useApp();
    const [name, setName] = useState<string>('');
    const [showDiff, setShowDiff] = useState<boolean>(false);

    async function switchBranch(target: string) {
        if (target !== branch && totalChanges > 0) {
            const proceed = await confirm({
                title: 'Discard unsaved changes?',
                body: (
                    <div className={'layout vertical gap'}>
                        <p>You have <strong>{totalChanges}</strong> unsaved change{totalChanges === 1 ? '' : 's'} on <strong>{branch}</strong>.</p>
                        <p>Switching to <strong>{target}</strong> will discard them.</p>
                    </div>
                ),
                confirmText: 'Discard and switch',
                danger: true,
            });
            if (!proceed) return;
        }
        await applyBranch(target);
        setViewBranch(false);
    }

    async function createNewBranch() {
        if (name === '') return;

        if (branches.find(b => b.name === name) !== undefined) {
            setNotification({
                title: 'Branch name already exists',
                message: `A branch named "${name}" already exists. Pick a different name.`,
            });
            return;
        }

        if (totalChanges > 0) {
            const proceed = await confirm({
                title: 'Carry unsaved changes to the new branch?',
                body: (
                    <div className={'layout vertical gap'}>
                        <p>You have <strong>{totalChanges}</strong> unsaved change{totalChanges === 1 ? '' : 's'} on <strong>{branch}</strong>.</p>
                        <p>Creating <strong>{name}</strong> will save those changes to the new branch as v1. <strong>{branch}</strong> will stay at its current saved version — your in-progress changes will NOT be applied to it.</p>
                    </div>
                ),
                confirmText: 'Create branch',
            });
            if (!proceed) return;
        }

        await addBranch(name);
    }

    return (
        <div className={'layout vertical overlay'} onClick={() => {
            setViewBranch(false);
        }}>
            <div className={'layout vertical gap box'} style={{padding: 10, gap: 10, width: 400}}
                 onClick={e => e.stopPropagation()}
            >
                <p className={'text header'}>Branches</p>
                <div className={'layout vertical gap'} style={{maxHeight: 200, overflowY: 'auto'}}>
                    {branches.map((b, index) => {
                        const isMain = b.name === 'Master';
                        const isSelected = b.name === branch;
                        return (
                            <div key={index}
                                 className={'layout horizontal center stretch label'}
                                 style={{
                                     border: `solid 1px ${isSelected ? 'var(--tint-color)' : 'var(--overlay-color)'}`,
                                     cursor: 'pointer',
                                     userSelect: 'none',
                                 }}
                                 onClick={() => switchBranch(b.name)}
                            >
                                <p>{`${b.name}, Version ${b.versionNumber}`}</p>
                                <button style={{opacity: isMain ? 0.3 : 1}} onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!isMain && await confirmDelete(b.name, 'branch')) {
                                        removeBranch(b.name);
                                    }
                                }}>
                                    <span className={'fa-solid fa-trash'}/>
                                </button>
                            </div>
                        )
                    })}
                </div>
                <button onClick={() => setShowDiff(true)} disabled={branches.length < 2}
                        style={{opacity: branches.length < 2 ? 0.5 : 1}}>
                    <span className={'fa-solid fa-code-compare'} style={{marginRight: 6}}/>
                    Compare with another branch…
                </button>
                <p className={'text header'}>New Branch</p>
                <StringInputLabel title={'Name'} value={name} placeholder={'Enter name...'} onChange={setName}/>
                <button onClick={async () => createNewBranch()}>Add</button>
            </div>
            {showDiff && <DiffView onClose={() => setShowDiff(false)}/>}
        </div>
    )
}

export default NotificationView;